import React, { useMemo, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import useToggle from "react-use/lib/useToggle";
import Select from "react-select";

import * as yup from "yup";

import { toast } from "react-toastify";
import { useMutation, useQuery } from "@apollo/client";
import { useHistory, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Input from "../../../../components/Input";

import {
  QUERY_MEMBER_GROUPS,
  QUERY_SHELLY_CONTROLLER,
} from "../../../../config/graphql/query";
import {
  UPDATE_SHELLY_CONTROLLER,
  CREATE_SHELLY_CONTROLLER,
  DELETE_SHELLY_CONTROLLER,
} from "../../../../config/graphql/mutation";

import useHasRole from "../../../../lib/hooks/useHasRole";
import { useClientId } from "../hooks/useClient";
import { useCurrentLanguage } from "../../../../lib/hooks/useCurrentLanguage";

import { reactSelectCustomStyles } from "../../../Employees/Employee/Information";

const shellyControlerRoles: ShellyControllerRole[] = ["DOOR", "BELL"];

interface FieldValues {
  title: string;
  role: ShellyControllerRole;
  host: string;
  authKey: string;
  deviceId: string;
  channel: number;
  timeout: number;
  doorAccess: {
    enabled: boolean;
    linkValidity?: number;
    usageLimit?: DoorAccessUsageLimitType;
    groups?: string[];
  };
}

const mapToFormValues = (shellyController: IShellyController): FieldValues => ({
  title: shellyController.title,
  role: shellyController.role,
  host: shellyController.host,
  authKey: shellyController.authKey,
  deviceId: shellyController.deviceId,
  channel: shellyController.channel,
  timeout: shellyController.timeout,
  doorAccess: {
    enabled: shellyController?.doorAccess?.enabled ?? false,
    linkValidity: shellyController?.doorAccess?.linkValidity ?? 15,
    usageLimit: shellyController?.doorAccess?.usageLimit ?? "SINGLE_USE",
    groups:
      shellyController?.doorAccess?.groups?.map((g) =>
        typeof g === "string" ? g : (g as any)?.id,
      ) ?? [],
  },
});

const ShellyController = React.memo(() => {
  const [show, setShow] = useToggle(false);

  const { t } = useTranslation(["client", "common"]);

  const history = useHistory();

  const { id } = useParams<{ id: string }>();

  const clientId = useClientId();

  const isAdmin = useHasRole(["ADMIN"]);
  const clientBasePath = isAdmin ? `/clients/${clientId}` : `/client`;

  const language = useCurrentLanguage();

  const schema = useMemo(
    () =>
      yup.object().shape({
        title: yup.string().required(t("client:client.shelly.yup.title")),
        role: yup
          .string()
          .oneOf(
            shellyControlerRoles,
            t("client:client.shelly.yup.role.invalid"),
          )
          .required(t("client:client.shelly.yup.role.required")),
        host: yup.string().required(t("client:client.shelly.yup.host")),
        authKey: yup.string().required(t("client:client.shelly.yup.authKey")),
        deviceId: yup.string().required(t("client:client.shelly.yup.deviceId")),
        channel: yup.number(),
        timeout: yup.number(),
        doorAccess: yup.object().shape({
          enabled: yup.boolean(),
          linkValidity: yup
            .number()
            .transform((value, originalValue) =>
              String(originalValue).trim() === "" ? undefined : value,
            ),
          usageLimit: yup
            .string()
            .oneOf(["SINGLE_USE", "UNLIMITED"])
            .required(t("client:client.shelly.yup.usageLimit")),
          groups: yup.array().of(yup.string()).nullable(),
        }),
      }),
    [t],
  );

  const methods = useForm<FieldValues>({
    resolver: yupResolver(schema),
    shouldFocusError: false,
    shouldUnregister: false,
    defaultValues: {
      doorAccess: {
        enabled: false,
        linkValidity: 15,
        usageLimit: "SINGLE_USE",
        groups: [],
      },
    },
  });

  const selectedRole = methods.watch("role");
  const employeeDoorAccessEnabled = methods.watch("doorAccess.enabled");

  const { data: groupsData } = useQuery<{
    memberGroups: Array<{ id: string; title: string }>;
  }>(QUERY_MEMBER_GROUPS, { fetchPolicy: "network-only" });

  const groupsSelectOptions = (groupsData?.memberGroups ?? []).map((g) => ({
    value: g.id,
    label: g.title,
  }));

  const { data: shellyControllerData } = useQuery<{
    shellyController: IShellyController;
  }>(QUERY_SHELLY_CONTROLLER, {
    skip: !id,
    variables: { id },
    onCompleted: ({ shellyController }) => {
      methods.reset(mapToFormValues(shellyController));
    },
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const data = shellyControllerData?.shellyController;

  const generatedUrl = `${window.location.origin}/access-portal?id=${data?.id}&lng=${language}`;

  const [onUpdate] = useMutation(UPDATE_SHELLY_CONTROLLER, {
    refetchQueries: [
      {
        query: QUERY_SHELLY_CONTROLLER,
        variables: { id },
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const [onCreate] = useMutation(CREATE_SHELLY_CONTROLLER, {
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const [onDelete] = useMutation(DELETE_SHELLY_CONTROLLER, {
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const onBeforeDelete = useCallback(() => {
    onDelete({ variables: { id } })
      .then(() => {
        toast.success<string>(t("client:client.shelly.toast.deleted"));
        history.replace(`${clientBasePath}/shelly`);
      })
      .catch(() => {
        toast.error<string>(t("client:client.shelly.toast.deletedError"));
      });

    setShow(false);
  }, [history, onDelete, id, t]);

  const onSubmit = useCallback(
    async (values: FieldValues) => {
      const { host, authKey, deviceId, role, doorAccess, ...rest } = values;

      const input = {
        ...(!id ? { host, authKey, deviceId } : { id }),
        role,
        ...rest,
        ...(role === "DOOR" && { doorAccess }),
      };

      if (!!id) {
        return onUpdate({ variables: { input } })
          .then(({ data: { updateShellyController } }) => {
            methods.reset(mapToFormValues(updateShellyController));
          })
          .then(() => {
            toast.success<string>(t("client:client.shelly.toast.updated"));
          })
          .catch(() => {
            toast.error<string>(t("client:client.shelly.toast.updatedError"));
          });
      }

      return onCreate({ variables: { input } })
        .then(
          ({
            data: {
              createShellyController: { id },
            },
          }) => {
            toast.success<string>(t("client:client.shelly.toast.created"));

            history.replace(`${clientBasePath}/shelly/${id}`);
          },
        )
        .catch(() => {
          toast.error<string>(t("client:client.shelly.toast.createdError"));
        });
    },
    [data, clientBasePath],
  );

  const handleCopyUrl = useCallback(() => {
    navigator.clipboard.writeText(generatedUrl).then(() => {
      toast.success<string>(t("client:client.shelly.toast.urlCopied"));
    });
  }, [generatedUrl, t]);

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item active" aria-current="page">
            <Link to="/clients">{t("client:client.token.nav.clients")}</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            <Link to={clientBasePath}>{data?.client?.title ?? "Client"}</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            <Link to={`${clientBasePath}/shelly`}>
              {t("client:client.shelly.nav.shelly")}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {data?.title ?? "Title"}
          </li>
        </ol>
      </nav>
      <FormProvider {...methods}>
        <form className="row" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="col-lg-4">
            <div className="form-group">
              <label htmlFor="title">
                {t("client:client.shelly.form.title")}
              </label>
              <Input name="title" className="form-control" />
            </div>
            <div className="form-group mt-3">
              <label htmlFor="role">
                {t("client:client.shelly.form.role")}
              </label>
              <Controller
                name="role"
                control={methods.control}
                render={({ field, fieldState }) => (
                  <>
                    <Select
                      id="role"
                      options={shellyControlerRoles.map((role) => ({
                        value: role,
                        label: role,
                      }))}
                      value={
                        field.value
                          ? {
                              value: field.value,
                              label: field.value,
                            }
                          : null
                      }
                      onChange={(selected) => field.onChange(selected?.value)}
                      onBlur={field.onBlur}
                      styles={reactSelectCustomStyles(!!fieldState.error)}
                    />
                    {fieldState.error && (
                      <div className="invalid-feedback d-block">
                        {fieldState.error.message}
                      </div>
                    )}
                  </>
                )}
              />
            </div>
            <div className="form-group">
              <label htmlFor="host">
                {t("client:client.shelly.form.host")}
              </label>
              <Input name="host" className="form-control" disabled={!!id} />
            </div>
            <div className="form-group">
              <label htmlFor="authKey">
                {t("client:client.shelly.form.authKey")}
              </label>
              <Input name="authKey" className="form-control" disabled={!!id} />
            </div>
            <div className="form-group">
              <label htmlFor="deviceId">
                {t("client:client.shelly.form.deviceId")}
              </label>
              <Input name="deviceId" className="form-control" disabled={!!id} />
            </div>
            <div className="form-group">
              <label htmlFor="channel">
                {t("client:client.shelly.form.channel")}
              </label>
              <Input name="channel" type="number" className="form-control" />
            </div>
            <div className="form-group">
              <label htmlFor="timeout">
                {t("client:client.shelly.form.timeout")}
              </label>
              <Input name="timeout" type="number" className="form-control" />
            </div>
          </div>
          <div className="col-lg-4">
            {selectedRole === "DOOR" && (
              <>
                <div className="form-group form-check mt-2">
                  <Controller
                    name="doorAccess.enabled"
                    control={methods.control}
                    render={({ field }) => (
                      <input
                        id="doorAccessEnabled"
                        type="checkbox"
                        className="form-check-input"
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        onBlur={field.onBlur}
                      />
                    )}
                  />
                  <label
                    className="form-check-label ml-2"
                    htmlFor="doorAccessEnabled"
                  >
                    {t("client:client.shelly.form.doorAccessEnabled")}
                  </label>
                </div>

                {employeeDoorAccessEnabled && (
                  <>
                    <div className="form-group mt-3">
                      <label htmlFor="doorAccess.groups">
                        {t(
                          "client:client.shelly.form.memberGroups",
                          "Allowed member groups",
                        )}
                      </label>
                      <Controller
                        name="doorAccess.groups"
                        control={methods.control}
                        render={({ field }) => (
                          <Select
                            id="doorAccess.groups"
                            isMulti
                            options={groupsSelectOptions}
                            value={groupsSelectOptions.filter((opt) =>
                              (field.value || []).includes(opt.value),
                            )}
                            onChange={(selected) => {
                              const values = (selected || []).map(
                                (s: any) => s.value,
                              );
                              field.onChange(values);
                            }}
                            classNamePrefix="react-select"
                          />
                        )}
                      />
                      <small className="form-text text-muted">
                        {t("client:client.shelly.help.groups")}
                      </small>
                    </div>
                    <div className="form-group mt-3">
                      <label htmlFor="doorAccess.linkValidity">
                        {t("client:client.shelly.form.linkValidity")}
                      </label>
                      <Controller
                        name="doorAccess.linkValidity"
                        control={methods.control}
                        render={({ field, fieldState }) => (
                          <>
                            <input
                              id="doorAccess.linkValidity"
                              type="number"
                              className={`form-control ${
                                fieldState.error ? "is-invalid" : ""
                              }`}
                              {...field}
                              onChange={(e) => field.onChange(e.target.value)}
                            />
                            {fieldState.error && (
                              <div className="invalid-feedback">
                                {fieldState.error.message}
                              </div>
                            )}
                          </>
                        )}
                      />
                      <small className="form-text text-muted">
                        {t("client:client.shelly.help.linkValidity")}
                      </small>
                    </div>
                    <div className="form-group mt-3">
                      <label htmlFor="doorAccess.usageLimit">
                        {t("client:client.shelly.form.usageLimit")}
                      </label>
                      <Controller
                        name="doorAccess.usageLimit"
                        control={methods.control}
                        render={({ field, fieldState }) => (
                          <>
                            <Select
                              id="doorAccess.usageLimit"
                              options={[
                                {
                                  value: "SINGLE_USE",
                                  label: t(
                                    "client:client.shelly.form.singleUse",
                                  ) as string,
                                },
                                {
                                  value: "UNLIMITED",
                                  label: t(
                                    "client:client.shelly.form.unlimited",
                                  ) as string,
                                },
                              ]}
                              value={
                                field.value
                                  ? {
                                      value: field.value,
                                      label: t(
                                        `client:client.shelly.form.${
                                          field.value === "SINGLE_USE"
                                            ? "singleUse"
                                            : "unlimited"
                                        }`,
                                      ) as string,
                                    }
                                  : null
                              }
                              onChange={(selected) =>
                                field.onChange(selected?.value)
                              }
                              styles={reactSelectCustomStyles(
                                !!fieldState.error,
                              )}
                            />
                            {fieldState.error && (
                              <div className="invalid-feedback d-block">
                                {fieldState.error.message}
                              </div>
                            )}
                          </>
                        )}
                      />
                      <small className="form-text text-muted">
                        {t("client:client.shelly.help.usageLimit")}
                      </small>
                    </div>
                    <div className="mt-4">
                      <label>
                        {t("client:client.shelly.form.employeeAccessPortalUrl")}
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={generatedUrl}
                          readOnly
                        />
                        <div className="input-group-append">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleCopyUrl}
                          >
                            {t("common:copy")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <div className="col-12">
            <input
              disabled={methods.formState.isSubmitting}
              type="submit"
              className="btn btn-primary mr-3"
              value={t(
                `client:client.shelly.button.${!!id ? "update" : "create"}`,
              )}
            />
            {!!id && (
              <button
                onClick={(e) => {
                  e.preventDefault();

                  setShow(true);
                }}
                className="btn btn-danger"
              >
                {t("common:delete")}
              </button>
            )}
          </div>
        </form>
      </FormProvider>
      <Modal
        size="sm"
        show={show}
        onHide={setShow}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("client:client.shelly.modal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("client:client.shelly.modal.body", { id })}</Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setShow(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default ShellyController;
